import { useCallback, useEffect, useRef } from "react";
import { Icon } from "../ui/Icon";
import { Tooltip } from "../ui/Tooltip.js";
import { ScrollingText } from "../ui/ScrollingText.js";
import { useAppSearch, type SearchResult } from "../../hooks/useAppSearch.js";
import { useMediaSearch } from "../../hooks/useMediaSearch.js";
import { useMediaQuery } from "../../hooks/useMediaQuery.js";
import { MediaSearchResultItem } from "./MediaSearchResultItem.js";

/**
 * Group results by category header for visual headers. The key is the outermost
 * navigable ancestor group (`headerId`/`headerLabel`) when present, else the
 * top-level nav section — so the three Dashboards (App, System, Streaming) head
 * separately, and a navigable group's nav children merge with its content
 * anchors under one header. Preserves score order within each group.
 */
function groupResultsBySection(results: SearchResult[]) {
  const map = new Map<string, { key: string; label: string; items: SearchResult[] }>();
  const order: string[] = [];

  for (const result of results) {
    const key = result.headerId ?? result.sectionId;
    let group = map.get(key);
    if (!group) {
      group = { key, label: result.headerLabel ?? result.sectionLabel, items: [] };
      map.set(key, group);
      order.push(key);
    }
    group.items.push(result);
  }

  return order.map((id) => map.get(id)!);
}

/** Truncate a string to maxLen characters with ellipsis. */
function truncate(str: string, maxLen: number) {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 1) + "\u2026";
}

/**
 * Builds a breadcrumb string for a search result.
 * e.g., "Settings > General Settings" or "Dashboard > Streaming Dashboard"
 */
function getBreadcrumb(result: SearchResult) {
  const parts: string[] = [result.sectionLabel];
  if (result.groupLabel) parts.push(result.groupLabel);
  return parts.join(" > ");
}

export const AppSearch = () => {
  const search = useAppSearch();
  const { mediaResults, mediaLoading, mediaError, mediaEnabled } = useMediaSearch(search.query);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  // Below Tailwind `md` (< 768px) we collapse the always-visible input to an
  // icon trigger that opens a full-width overlay. Complements `md:` (>= 768px),
  // following the convention of useIsMobile/useIsDesktop in the same hook file.
  const isMobile = useMediaQuery("(max-width: 767px)");
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close the search and, on mobile, return focus to the trigger button.
  // On desktop the trigger is not rendered, so the focus() call is a no-op and
  // desktop behaviour is unchanged.
  const closeSearch = useCallback(() => {
    search.handleClose();
    triggerRef.current?.focus();
  }, [search.handleClose]);

  // Total navigable items across page results + media results
  const pageCount = search.results.length;
  const mediaCount = mediaEnabled ? mediaResults.length : 0;
  const totalResults = pageCount + mediaCount;

  const hasAnyResults = search.hasResults || mediaResults.length > 0 || mediaLoading;

  // Only reference aria-activedescendant when the pointed-to element actually
  // exists in the DOM (i.e., an option button, not a loading/empty text div).
  const activeDescendantValid =
    search.open &&
    totalResults > 0 &&
    search.selectedIndex < totalResults;

  // -------------------------------------------------------------------------
  // Keyboard navigation — unified across page + media results
  // -------------------------------------------------------------------------

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!search.open) return;
      if (totalResults === 0) {
        // Still allow Escape when no results
        if (event.key === "Escape") {
          event.preventDefault();
          closeSearch();
        }
        return;
      }

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          search.setSelectedIndex((prev: number) =>
            prev < totalResults - 1 ? prev + 1 : 0
          );
          break;
        }
        case "ArrowUp": {
          event.preventDefault();
          search.setSelectedIndex((prev: number) =>
            prev > 0 ? prev - 1 : totalResults - 1
          );
          break;
        }
        case "Enter": {
          event.preventDefault();
          const idx = search.selectedIndex;
          if (idx < pageCount) {
            // Page result
            const selected = search.results[idx];
            if (selected) {
              search.handleNavigate(selected.item.path, selected.item.external);
            }
          } else {
            // Media result
            const mediaIdx = idx - pageCount;
            const selected = mediaResults[mediaIdx];
            if (selected) {
              search.handleNavigate(
                `/streaming/dashboard/control-center/media/item/${selected.serverType}/${selected.id}`
              );
            }
          }
          break;
        }
        case "Escape": {
          event.preventDefault();
          closeSearch();
          break;
        }
      }
    },
    [
      search.open,
      search.results,
      search.selectedIndex,
      search.handleNavigate,
      closeSearch,
      search.setSelectedIndex,
      totalResults,
      pageCount,
      mediaResults,
    ]
  );

  // Clamp selectedIndex when total changes (e.g., media results arrive/depart)
  const prevTotalRef = useRef(totalResults);
  if (prevTotalRef.current !== totalResults) {
    prevTotalRef.current = totalResults;
    if (totalResults > 0 && search.selectedIndex >= totalResults) {
      search.setSelectedIndex(totalResults - 1);
    }
  }

  // -------------------------------------------------------------------------
  // Global shortcuts + click-outside + scroll-into-view
  // -------------------------------------------------------------------------

  // Global keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        if (search.open) {
          search.handleClose();
        } else {
          search.handleOpen();
        }
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [search.open, search.handleClose, search.handleOpen]);

  // Click outside to close
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        search.containerRef.current &&
        !search.containerRef.current.contains(event.target as Node)
      ) {
        search.handleClose();
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [search.handleClose]);

  // Scroll selected item into view
  useEffect(() => {
    const el = itemRefs.current.get(search.selectedIndex);
    if (el) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [search.selectedIndex]);

  const setItemRef = useCallback(
    (index: number) => (el: HTMLButtonElement | null) => {
      if (el) {
        itemRefs.current.set(index, el);
      } else {
        itemRefs.current.delete(index);
      }
    },
    []
  );

  // -------------------------------------------------------------------------
  // Grouping + render
  // -------------------------------------------------------------------------

  const grouped = groupResultsBySection(search.results);

  // Build a flat index mapping so we can track which global index each item has.
  // This counter increments through page results first, then media results.
  let globalIndex = 0;

  // Whether the query is long enough for media search
  const showMediaSection = mediaEnabled && search.query.trim().length >= 2;

  // The text input is identical across mobile + desktop presentations; only its
  // surrounding chrome (⌘K hint vs close button) differs, so it is extracted
  // once to avoid duplicating the combobox wiring.
  const inputEl = (
    <input
      ref={search.inputRef}
      value={search.query}
      onChange={(event) => {
        search.setQuery(event.target.value);
        search.setOpen(true);
      }}
      onFocus={() => search.setOpen(true)}
      onKeyDown={handleKeyDown}
      placeholder={mediaEnabled ? "Search pages and media..." : "Search pages..."}
      className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
      role="combobox"
      aria-label={
        mediaEnabled
          ? "Search dashboard pages and media"
          : "Search dashboard pages"
      }
      aria-expanded={search.open}
      aria-controls="app-search-listbox"
      aria-activedescendant={
        activeDescendantValid
          ? `app-search-item-${search.selectedIndex}`
          : undefined
      }
      aria-autocomplete="list"
    />
  );

  // The results panel (header + list + live region + footer) is identical in
  // both presentations; only its positioning wrapper differs.
  const dropdownInner = (
    <>
      {/* Header label */}
          <div className="border-b border-border/60 px-3 py-2 text-xs font-medium text-muted-foreground">
            {search.isEmptyQuery ? "Suggestions" : `Results for "${truncate(search.query.trim(), 30)}"`}
          </div>

          {/* Results list */}
          <div
            ref={listRef}
            className="max-h-[400px] overflow-y-auto qb-scroll"
            role="listbox"
            id="app-search-listbox"
          >
            {/* Empty state (no page results AND no media results/loading) */}
            {!search.hasResults && !(showMediaSection && (mediaResults.length > 0 || mediaLoading)) ? (
              <div role="presentation" className="px-4 py-6 text-center text-sm text-muted-foreground">
                {search.isEmptyQuery
                  ? "Start typing to search pages..."
                  : `No results found for "${truncate(search.query.trim(), 40)}"`}
              </div>
            ) : (
              <>
                {/* Page result groups */}
                {grouped.map((group) => (
                  <div
                    key={`group-${group.key}`}
                    role="group"
                    aria-label={group.label}
                  >
                    <div
                      role="presentation"
                      className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm border-b border-border/30 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                      {group.label}
                    </div>

                    {group.items.map((result) => {
                      const idx = globalIndex++;
                      const isSelected = idx === search.selectedIndex;
                      const breadcrumb = getBreadcrumb(result);

                      return (
                        <button
                          key={result.item.id}
                          ref={setItemRef(idx)}
                          id={`app-search-item-${idx}`}
                          role="option"
                          aria-selected={isSelected}
                          type="button"
                          onClick={() =>
                            search.handleNavigate(result.item.path, result.item.external)
                          }
                          onMouseEnter={() => search.setSelectedIndex(idx)}
                          className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
                            isSelected
                              ? "bg-primary/10 text-foreground"
                              : "text-foreground hover:bg-muted/60"
                          }`}
                        >
                          {/* Icon */}
                          {result.item.icon && (
                            <Icon
                              icon={result.item.icon}
                              className={`h-4 w-4 flex-shrink-0 ${
                                isSelected ? "text-primary" : "text-muted-foreground"
                              }`}
                            />
                          )}

                          {/* Label — show the abbreviated form when provided; the
                              full title stays available via the tooltip. */}
                          <div className="min-w-0 flex-1">
                            <Tooltip content={result.item.label} side="top" delay={400} className="min-w-0">
                              <ScrollingText
                                text={result.item.shortLabel ?? result.item.label}
                                className="text-sm"
                              />
                            </Tooltip>
                          </div>

                          {/* Description (icon + tooltip) */}
                          {result.description && (
                            <Tooltip content={result.description} side="bottom" delay={300} maxWidth="sm">
                              <Icon
                                icon="lucide:file-text"
                                aria-hidden="true"
                                className="hidden h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/40 sm:block"
                              />
                            </Tooltip>
                          )}

                          {/* Breadcrumb (icon + tooltip) */}
                          <Tooltip content={breadcrumb} side="left" delay={200}>
                            <Icon
                              icon="lucide:map-pin"
                              aria-hidden="true"
                              className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/40"
                            />
                          </Tooltip>

                          {/* Enter hint on selected */}
                          {isSelected && (
                            <span className="flex-shrink-0 text-xs text-muted-foreground">
                              &#x21B5;
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}

                {/* Media Results Section */}
                {showMediaSection && (
                  <div role="group" aria-label="Media">
                    {/* Section header */}
                    <div
                      role="presentation"
                      className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm border-b border-border/30 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"
                    >
                      <span>Media</span>
                      {mediaLoading && (
                        <span className="h-2 w-2 rounded-full bg-primary/60 animate-pulse" />
                      )}
                    </div>

                    {/* Media results or empty/loading/error states */}
                    {mediaLoading && mediaResults.length === 0 ? (
                      <div role="presentation" className="px-4 py-3 text-center text-xs text-muted-foreground">
                        Searching media libraries...
                      </div>
                    ) : mediaError ? (
                      <div role="presentation" className="px-4 py-3 text-center text-xs text-muted-foreground">
                        Media search unavailable
                      </div>
                    ) : mediaResults.length === 0 && !mediaLoading ? (
                      <div role="presentation" className="px-4 py-3 text-center text-xs text-muted-foreground">
                        No media found
                      </div>
                    ) : (
                      mediaResults.map((item, i) => {
                        const idx = globalIndex++;
                        const isSelected = idx === search.selectedIndex;
                        return (
                          <MediaSearchResultItem
                            key={`${item.serverType}-${item.id}`}
                            ref={setItemRef(idx)}
                            item={item}
                            isSelected={isSelected}
                            index={idx}
                            onSelect={() => {
                              search.handleNavigate(
                                `/streaming/dashboard/control-center/media/item/${item.serverType}/${item.id}`
                              );
                            }}
                            onHover={() => search.setSelectedIndex(idx)}
                          />
                        );
                      })
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Screen reader live region for result counts */}
          <span className="sr-only" aria-live="polite" aria-atomic="true">
            {search.query.trim().length > 0 && !search.isEmptyQuery
              ? [
                  pageCount > 0 && `${pageCount} page ${pageCount === 1 ? "result" : "results"}`,
                  showMediaSection && mediaLoading && "Searching media",
                  showMediaSection && !mediaLoading && mediaCount > 0 && `${mediaCount} media ${mediaCount === 1 ? "result" : "results"}`,
                  showMediaSection && !mediaLoading && mediaCount === 0 && !mediaError && "No media found",
                  totalResults === 0 && !mediaLoading && "No results found",
                ].filter(Boolean).join(". ")
              : ""}
          </span>

          {/* Footer with keyboard hints */}
          {(search.hasResults || mediaResults.length > 0) && (
            <div className="flex items-center gap-3 border-t border-border/40 px-3 py-1.5 text-[10px] text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <kbd className="rounded border border-border/50 bg-muted/50 px-1">&#x2191;</kbd>
                <kbd className="rounded border border-border/50 bg-muted/50 px-1">&#x2193;</kbd>
                navigate
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="rounded border border-border/50 bg-muted/50 px-1">&#x21B5;</kbd>
                open
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="rounded border border-border/50 bg-muted/50 px-1">esc</kbd>
                close
              </span>
            </div>
          )}
    </>
  );

  // ---- Mobile (< md): icon trigger + full-width overlay ----
  if (isMobile) {
    return (
      <div ref={search.containerRef} className="flex items-center">
        <button
          ref={triggerRef}
          type="button"
          aria-label="Search"
          aria-expanded={search.open}
          onClick={() => search.handleOpen()}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border/50 bg-muted/70 text-muted-foreground shadow-subtle transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
        >
          <Icon icon="lucide:search" className="h-4 w-4" />
        </button>

        {search.open && (
          <div
            className="fixed inset-x-0 top-0 z-50 flex flex-col gap-2 border-b border-border/50 bg-card/95 px-3 py-2 backdrop-blur-xl animate-in fade-in-0 duration-100"
            style={{ minHeight: "var(--topbar-height)" }}
          >
            {/* Full-width search input row */}
            <div className="flex items-center gap-3 rounded-md border border-border/50 bg-muted/70 px-3 py-2 text-sm text-muted-foreground shadow-subtle focus-within:ring-2 focus-within:ring-primary/70">
              <Icon icon="lucide:search" className="h-4 w-4 flex-shrink-0" />
              {inputEl}
              <button
                type="button"
                aria-label="Close search"
                onClick={closeSearch}
                className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
              >
                <Icon icon="lucide:x" className="h-4 w-4" />
              </button>
            </div>

            {/* Results dropdown */}
            <div className="overflow-hidden rounded-lg border border-border/50 bg-card/95 shadow-soft backdrop-blur-sm">
              {dropdownInner}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- Desktop (>= md): inline input, unchanged ----
  return (
    <div
      ref={search.containerRef}
      className="relative w-full max-w-[340px] sm:max-w-[480px]"
    >
      {/* Search input */}
      <div className="flex items-center gap-3 rounded-md border border-border/50 bg-muted/70 px-3 py-2 text-sm text-muted-foreground shadow-subtle transition-shadow focus-within:ring-2 focus-within:ring-primary/70">
        <Icon icon="lucide:search" className="h-4 w-4 flex-shrink-0" />
        {inputEl}
        <kbd className="hidden items-center gap-1 rounded-md border border-border/50 bg-card/60 px-2 py-0.5 text-[10px] text-muted-foreground sm:inline-flex">
          ⌘K
        </kbd>
      </div>

      {/* Dropdown */}
      {search.open && (
        <div className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-lg border border-border/50 bg-card/95 shadow-soft backdrop-blur-sm animate-in fade-in-0 zoom-in-95 duration-100">
          {dropdownInner}
        </div>
      )}
    </div>
  );
};
